import "./RegularLayout.scss"

import {
  BannerNotification,
  Footer,
  FooterVariant,
  TableData,
  toast,
} from "@skylead/component-library"
import classNames from "classnames"
import copy from "copy-to-clipboard"
import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { HELP_LINKS } from "../../../constants/help-center"
import { SIDEBAR_MAX_WIDTH } from "../../../constants/sidebar"
import * as appActions from "../../../redux/actions"
import { useGlobalTranslation } from "../../../utils/hook"
import { validateGenerateRegisterLink } from "../../../utils/validation/validation-utils"
import Modal, { ModalVariant } from "../../atoms/Modal"
import Header from "../../organisms/Header/Header"
import InputGroup from "../../organisms/InputGroup"
import { NavigationVariant } from "../../organisms/Navigation"
import Sidebar from "../../organisms/Sidebar"

const RegularLayout = ({
  children,
  title = "",
  leftTitleIcon,
  rightTitleIcon,
  navType = NavigationVariant.SEAT,
  contentSwitcherItems,
  rightContentSwitcherItems,
  leftHeaderButton,
  rightHeaderButton,
  headerSearch,
  tableData,
  topHeaderTabBarItems,
  headerTabBarItems,
  contentBanners,
  filter,
  multiSelect,
  rightFilter,
  leftHeaderContent,
  statisticsFilters,
  loadingSeats = false,
  setOnlyBuySeat = () => {},
  showTransferSeatsModal,
  footerData,
  withoutOverflow = false,
  campaignStatusBar,
  keyMaxOnPage,
}) => {
  const dispatch = useDispatch()
  const { tt } = useGlobalTranslation()

  const { selectedRows, formData, whitelabelId } = useSelector(state => ({
    selectedRows: state.forms.formData.selectedRows,
    formData: state.forms.formData,
    whitelabelId: state.user.profile.whitelabelId,
  }))

  const initialSidebarWidth = useMemo(() => {
    return navType === NavigationVariant.TEAM
      ? SIDEBAR_MAX_WIDTH
      : +localStorage.getItem("sidebarWidth") || SIDEBAR_MAX_WIDTH
  }, [])

  const [generatedLink, setGeneratedLink] = useState("")
  const [navbarWidth, setNavbarWidth] = useState(initialSidebarWidth)
  const [selectedMaxOnPage, setSelectedMaxOnPage] = useState(tableData?.maxOnPage)
  const [showGenerateLinkModalFlag, setShowGenerateLinkModalFlag] = useState(false)

  const handleChangeMaxOnPage = leadPerPage => {
    setSelectedMaxOnPage(leadPerPage)
    localStorage.setItem(keyMaxOnPage, leadPerPage)
    tableData.maxOnPage = leadPerPage
    tableData.handleChangeMaxOnPage(leadPerPage)
  }

  const copyGeneratedURL = () => {
    copy(generatedLink)
    toast.success(tt("regular.layout.copy-url.toast.success"))
  }

  const generateLinkUrl = async () => {
    const { generateRegistrationLinkCount } = formData

    dispatch(appActions.clearFormErrors())

    const allErrors = validateGenerateRegisterLink({ generateRegistrationLinkCount })
    dispatch(appActions.setFormErrors(allErrors))

    if (allErrors) return false

    const generatedLinkData = await dispatch(
      appActions.generateRegistrationLink(generateRegistrationLinkCount),
    )

    setGeneratedLink(generatedLinkData)
  }

  const generateUrlLinkData = {
    show: showGenerateLinkModalFlag,
    onClick: () => setShowGenerateLinkModalFlag(true),
  }

  const layoutContainerClass = classNames("layout-content", {
    "without-overflow": withoutOverflow,
  })

  const handleOnSelectedRowsChange = sr => {
    dispatch(appActions.updateFormField("selectedRows", sr))
  }

  return (
    <div className="layout-container-wrapper">
      <div className="layout-container">
        <Sidebar
          navbarWidth={navbarWidth}
          setNavbarWidth={setNavbarWidth}
          navType={navType}
          loadingSeats={loadingSeats}
          setOnlyBuySeat={setOnlyBuySeat}
          showTransferSeatsModal={showTransferSeatsModal}
          generateUrlLinkData={generateUrlLinkData}
        />

        <div className="layout-content-container">
          <Header
            title={title}
            leftTitleIcon={leftTitleIcon}
            rightTitleIcon={rightTitleIcon}
            contentSwitcherItems={contentSwitcherItems}
            rightContentSwitcherItems={rightContentSwitcherItems}
            leftHeaderButton={leftHeaderButton}
            rightHeaderButton={rightHeaderButton}
            headerSearch={headerSearch}
            topHeaderTabBarItems={topHeaderTabBarItems}
            headerTabBarItems={headerTabBarItems}
            filter={filter}
            multiSelect={multiSelect}
            rightFilter={rightFilter}
            leftHeaderContent={leftHeaderContent}
            statisticsFilters={statisticsFilters}
            landingHeaderActions={navType === NavigationVariant.TEAM}
            campaignStatusBar={campaignStatusBar}
          />
          {contentBanners?.map(contentBanner => {
            return contentBanner?.display ? (
              <BannerNotification
                type={contentBanner.type}
                label={contentBanner.label}
                showButton={contentBanner.showButton}
                buttonLabel={contentBanner.buttonLabel}
                onClick={contentBanner.onClick}
                disabled={contentBanner.disabled}
              />
            ) : null
          })}
          <div className={layoutContainerClass}>
            {tableData && (
              <TableData
                accordionRenderer={tableData.accordionRenderer}
                tableAriaLabel={tableData.tableAriaLabel}
                identifier={tableData.identifier}
                column={tableData.column}
                columns={tableData.columns}
                selectSort={tableData.selectSort}
                sort={tableData.sort}
                page={tableData.page}
                count={tableData.count}
                changePage={tableData.changePage}
                maxOnPage={tableData.maxOnPage}
                data={tableData.data}
                loading={tableData.loading}
                emptyState={tableData.emptyState}
                multiSelectActions={tableData.multiSelectActions}
                selectedMaxOnPage={selectedMaxOnPage}
                handleChangeMaxOnPage={handleChangeMaxOnPage}
                columnSortOrder={tableData.columnSortOrder}
                setColumnSortOrder={tableData.setColumnSortOrder}
                selectedRows={selectedRows}
                dispatch={dispatch}
                updateFormField={appActions.updateFormField}
                teamRolesHelpLink={HELP_LINKS.WHAT_ARE_TEAM_ROLES_AND_HOW_TO_ASSIGN_THEM}
                updateFormFields={appActions.updateFormFields}
                formData={formData}
                whitelabelId={whitelabelId}
                tt={tt}
                onSelectedRowsChange={handleOnSelectedRowsChange}
              />
            )}
            {children}
          </div>
          {footerData && footerData?.show && (
            <Footer type={footerData.type} buttons={footerData.buttons} />
          )}
        </div>
      </div>

      {showGenerateLinkModalFlag && (
        <Modal
          show={showGenerateLinkModalFlag}
          type={ModalVariant.SMALL}
          title={tt("generate-registration-url")}
          content={
            <div>
              <InputGroup
                type="number"
                field="generateRegistrationLinkCount"
                label={tt("regular.layout.modal.input.label")}
                description={tt("regular.layout.modal.input.msg")}
              />
              <InputGroup customValue={generatedLink} label={tt("registration-url")} readOnly />
            </div>
          }
          footerVariant={FooterVariant.TRANSACTIONAL}
          primaryBtn={{
            label: generatedLink.length === 0 ? tt("generate-url") : tt("copy-url"),
            onClick: generatedLink.length === 0 ? generateLinkUrl : copyGeneratedURL,
          }}
          secondaryBtn={{
            label: tt("close"),
            onClick: () => {
              dispatch(appActions.closeModal())
              setShowGenerateLinkModalFlag(false)
            },
          }}
          noHideOnSubmit
        />
      )}
    </div>
  )
}

// RegularLayout.propTypes = {
//   children: PropTypes.node,
//   title: PropTypes.string,
//   navType: PropTypes.string,
//   contentSwitcherItems: PropTypes.shape({}),
//   rightContentSwitcherItems: PropTypes.shape({}),
//   leftHeaderButton: PropTypes.shape({}),
//   rightHeaderButton: PropTypes.shape({}),
//   headerSearch: PropTypes.shape({}),
//   tableData: PropTypes.shape({}),
//   topHeaderTabBarItems: PropTypes.arrayOf(PropTypes.shape({})),
//   headerTabBarItems: PropTypes.arrayOf(PropTypes.shape({})),
//   loadingSeats: PropTypes.bool,
//   setOnlyBuySeat: PropTypes.func,
//   footerData: PropTypes.shape({}),
//   withoutOverflow: PropTypes.bool,
// }

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.children === nextProps.children &&
    prevProps.title === nextProps.title &&
    prevProps.navType === nextProps.navType &&
    prevProps.contentSwitcherItems === nextProps.contentSwitcherItems &&
    prevProps.rightContentSwitcherItems === nextProps.rightContentSwitcherItems &&
    prevProps.leftHeaderButton === nextProps.leftHeaderButton &&
    prevProps.rightHeaderButton === nextProps.rightHeaderButton &&
    prevProps.headerSearch === nextProps.headerSearch &&
    prevProps.tableData === nextProps.tableData &&
    prevProps.topHeaderTabBarItems === nextProps.topHeaderTabBarItems &&
    prevProps.headerTabBarItems === nextProps.headerTabBarItems &&
    prevProps.contentBanners === nextProps.contentBanners &&
    prevProps.filter === nextProps.filter &&
    prevProps.multiSelect === nextProps.multiSelect &&
    prevProps.rightFilter === nextProps.rightFilter &&
    prevProps.leftHeaderContent === nextProps.leftHeaderContent &&
    prevProps.statisticsFilters === nextProps.statisticsFilters &&
    prevProps.loadingSeats === nextProps.loadingSeats &&
    prevProps.setOnlyBuySeat === nextProps.setOnlyBuySeat &&
    prevProps.showTransferSeatsModal === nextProps.showTransferSeatsModal &&
    prevProps.footerData === nextProps.footerData &&
    prevProps.withoutOverflow === nextProps.withoutOverflow &&
    prevProps.campaignStatusBar === nextProps.campaignStatusBar
  )
}

export default React.memo(RegularLayout, areEqual)
